import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Undo as UndoIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useToast } from '../../hooks/useToast';
import { appendCashReconLedgerCoaQueryParams } from './cashReconLedgerScope';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateFormFieldsSx,
  harmonateDataGridSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
} from '../../../theme/harmonateStyles';

const Reclassification = ({ onBack }) => {
  const { toastState, showSuccess, showError, showInfo, hideToast } = useToast();

  // State for filters
  const [selectedProperty, setSelectedProperty] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // State for properties list
  const [properties, setProperties] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);

  // State for reclassified transactions
  const [reclassifiedRows, setReclassifiedRows] = useState([]);
  const [reclassifiedLoading, setReclassifiedLoading] = useState(false);
  const [reclassifiedError, setReclassifiedError] = useState(null);

  // State for pagination
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 100 });
  const [rowCount, setRowCount] = useState(0);

  // Undo confirmation dialog
  const [undoDialogOpen, setUndoDialogOpen] = useState(false);
  const [undoTransactionId, setUndoTransactionId] = useState(null);
  const [undoInProgress, setUndoInProgress] = useState(false);

  // Fetch properties list
  const fetchProperties = async () => {
    try {
      setPropertiesLoading(true);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token) {
        throw new Error('No authentication token found');
      }
      if (!tenantId) {
        throw new Error('No tenant ID found');
      }
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';

      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/user-tenant-properties?tenant_id=${tenantId}&user_id=${localStorage.getItem('user_id')}`;

      const resp = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        throw new Error(`Failed to fetch properties: ${resp.status} ${resp.statusText}`);
      }

      const data = await resp.json();

      if (Array.isArray(data)) {
        const mappedData = data.map(property => ({
          PROPERTY_ID: property.propertyId,
          PROPERTY_NAME: property.propertyName,
          PROPERTY_CODE: property.propertyCode,
          ...property
        }));
        setProperties(mappedData);
      } else {
        throw new Error('Invalid response format from properties API');
      }
    } catch (err) {
      console.error('Error fetching properties:', err);
      setProperties([]);
      showError('Failed to load properties');
    } finally {
      setPropertiesLoading(false);
    }
  };

  // Fetch reclassified transactions (From Date and To Date are optional)
  const fetchReclassifiedTransactions = async () => {
    if (!selectedProperty) {
      showError('Please select Property to load data');
      return;
    }

    try {
      setReclassifiedLoading(true);
      setReclassifiedError(null);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');

      if (!token) {
        throw new Error('No authentication token found');
      }
      if (!tenantId) {
        throw new Error('No tenant ID found');
      }
      const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4003';

      // Get property ID from properties list
      const selectedProp = properties.find(p => p.PROPERTY_NAME === selectedProperty || p.propertyName === selectedProperty);
      const propertyId = selectedProp?.PROPERTY_ID || selectedProp?.propertyId || selectedProperty;

      let apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/gl-transactions/deleted/by-tenant-with-date-range/${tenantId}?propertyId=${propertyId}`;
      if (fromDate) apiUrl += `&startDate=${fromDate}`;
      if (toDate) apiUrl += `&endDate=${toDate}`;
      apiUrl = await appendCashReconLedgerCoaQueryParams(apiUrl, { baseUrl, tenantId, token });

      const resp = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        throw new Error(`Failed to fetch reclassified transactions: ${resp.status} ${resp.statusText}`);
      }

      const data = await resp.json();

      if (Array.isArray(data)) {
        // Map the API response to the expected format for the grid
        const mappedData = data.map(item => ({
          id: item.id,
          description: item.description,
          date: item.transactionDate,
          docNo: item.referenceNo,
          debit: item.debitAmount || 0,
          credit: item.creditAmount || 0,
          glAccountNo: item.glAccountNo,
          glAccountName: item.glAccountName,
          amount: item.amount || 0,
          txnDirection: item.txnDirection,
          currency: item.currency,
          sourceSystem: item.sourceSystem,
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
          isDeleted: item.isDeleted,
          // Keep original data for reference
          originalData: item,
        }));

        setReclassifiedRows(mappedData);
        setRowCount(mappedData.length);
      } else {
        throw new Error('Invalid response format from reclassified transactions API');
      }
    } catch (err) {
      console.error('Error fetching reclassified transactions:', err);
      setReclassifiedError(err.message || 'Failed to fetch reclassified transactions');
      setReclassifiedRows([]);
      setRowCount(0);
      showError(err.message || 'Failed to fetch reclassified transactions');
    } finally {
      setReclassifiedLoading(false);
    }
  };

  // Open undo confirmation dialog
  const handleUndoClick = (transactionId) => {
    setUndoTransactionId(transactionId);
    setUndoDialogOpen(true);
  };

  // Close undo dialog without restoring
  const handleUndoDialogClose = () => {
    if (!undoInProgress) {
      setUndoDialogOpen(false);
      setUndoTransactionId(null);
    }
  };

  // Handle restore (undo reclassification) - called after user confirms
  const handleRestore = async () => {
    if (!undoTransactionId) return;
    const transactionId = undoTransactionId;
    try {
      setUndoInProgress(true);
      const token = localStorage.getItem('token');

      if (!token) {
        throw new Error('No authentication token found');
      }

      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/gl-transactions/restore/${transactionId}`;

      const response = await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to restore transaction: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Restore response:', data);

      showSuccess('Transaction restored successfully');
      setUndoDialogOpen(false);
      setUndoTransactionId(null);

      // Refresh the reclassified transactions list
      fetchReclassifiedTransactions();
    } catch (err) {
      console.error('Error restoring transaction:', err);
      showError(err.message || 'Failed to restore transaction');
    } finally {
      setUndoInProgress(false);
    }
  };

  // Load properties on mount
  useEffect(() => {
    fetchProperties();
  }, []);

  // Column definitions for reclassified transactions grid
  const columns = [
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => params.row.description || '',
    },
    {
      field: 'date',
      headerName: 'Date',
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => params.row.date || '',
    },
    {
      field: 'docNo',
      headerName: 'Doc-No',
      flex: 1,
      minWidth: 120,
      valueGetter: (params) => params.row.docNo || '',
    },
    // {
    //   field: 'glAccountNo',
    //   headerName: 'GL Account No',
    //   flex: 1,
    //   minWidth: 120,
    //   valueGetter: (params) => params.row.glAccountNo || '',
    // },
    // {
    //   field: 'glAccountName',
    //   headerName: 'GL Account Name',
    //   flex: 1,
    //   minWidth: 150,
    //   valueGetter: (params) => params.row.glAccountName || '',
    // },
    {
      field: 'debit',
      headerName: 'Debit',
      flex: 1,
      minWidth: 120,
      type: 'number',
      valueGetter: (params) => params.row.debit || 0,
      valueFormatter: (params) => {
        const value = params.value || 0;
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
        }).format(value);
      },
    },
    {
      field: 'credit',
      headerName: 'Credit',
      flex: 1,
      minWidth: 120,
      type: 'number',
      valueGetter: (params) => params.row.credit || 0,
      valueFormatter: (params) => {
        const value = params.value || 0;
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
        }).format(value);
      },
    },
    // {
    //   field: 'sourceSystem',
    //   headerName: 'Source System',
    //   flex: 1,
    //   minWidth: 120,
    //   valueGetter: (params) => params.row.sourceSystem || '',
    // },
    // {
    //   field: 'createdAt',
    //   headerName: 'Created At',
    //   flex: 1,
    //   minWidth: 150,
    //   valueGetter: (params) => params.row.createdAt || '',
    // },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="Undo Reclassification">
          <IconButton
            size="small"
            onClick={() => handleUndoClick(params.row.id)}
            sx={{
              color: ht.green,
              '&:hover': {
                backgroundColor: ht.secondaryTab,
              },
            }}
          >
            <UndoIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box
      sx={{ ...harmonatePageSx, p: 3 }}
    >
      {/* Header - same as Compare */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={onBack}
            sx={{ color: ht.white, '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.12)' } }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={harmonatePageTitleSx}>
            Cash Reconciliation - Reclassification
          </Typography>
        </Box>
      </Box>

      {/* Filters — oval fields like Output */}
      <Paper
        elevation={0}
        sx={{ ...harmonateCardSx, ...harmonateFormFieldsSx, p: 2, mb: 2, bgcolor: ht.white, flexShrink: 0 }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Autocomplete
            disabled={propertiesLoading}
            options={properties}
            getOptionLabel={(option) => {
              const code = option.PROPERTY_CODE || option.propertyCode || '';
              const name = option.PROPERTY_NAME || option.propertyName || '';
              return code && name ? `${code} - ${name}` : name || code || '';
            }}
            value={properties.find(p => (p.PROPERTY_NAME || p.propertyName) === selectedProperty) || null}
            onChange={(event, newValue) => {
              setSelectedProperty(newValue ? (newValue.PROPERTY_NAME || newValue.propertyName) : '');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Property / Hotel"
                size="small"
                placeholder="Select Property"
                sx={{ minWidth: 300 }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {propertiesLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        params.InputProps.endAdornment
                      )}
                    </>
                  )
                }}
              />
            )}
            isOptionEqualToValue={(option, value) =>
              (option.PROPERTY_NAME || option.propertyName) === (value?.PROPERTY_NAME || value?.propertyName)
            }
            sx={{ fontSize: '14px' }}
          />
          <TextField
            size="small"
            label="From Date"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
          <TextField
            size="small"
            label="To Date"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={fetchReclassifiedTransactions}
            disabled={reclassifiedLoading || !selectedProperty}
            sx={harmonateProceedButtonSx}
          >
            {reclassifiedLoading ? 'Loading...' : 'Load'}
          </Button>
        </Box>
      </Paper>

      {/* Error Alert - same as Compare */}
      {reclassifiedError && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }} onClose={() => setReclassifiedError(null)}>
          {reclassifiedError}
        </Alert>
      )}

      {/* Content area - same as Compare (loading spinner or Paper with grid) */}
      {reclassifiedLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 1,
            minHeight: 0,
          }}
        >
          <CircularProgress sx={{ color: ht.green }} />
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{ ...harmonateCardSx, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: ht.white }}
        >
          <DataGridPro
            rows={reclassifiedRows}
            columns={columns}
            rowCount={rowCount}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pagination
            paginationMode="client"
            loading={false}
            rowHeight={40}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            sx={{
              ...harmonateDataGridSx,
              flex: 1,
              minHeight: 0,
              '& .MuiDataGrid-row:hover': {
                bgcolor: ht.secondaryTab,
              },
            }}
            slots={{
              noRowsOverlay: () => (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', p: 3 }}>
                  <Typography variant="body1" color="text.secondary">
                    Select filters and click Load to view reclassified transactions
                  </Typography>
                </Box>
              ),
            }}
          />
        </Paper>
      )}

      {/* Undo reclassification confirmation dialog */}
      <Dialog
        open={undoDialogOpen}
        onClose={handleUndoDialogClose}
        maxWidth="xs"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Undo Reclassification</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography variant="body1">
            Are you sure you want to undo this reclassification? The transaction will be restored.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleUndoDialogClose}
            disabled={undoInProgress}
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRestore}
            disabled={undoInProgress}
            sx={harmonateProceedButtonSx}
          >
            {undoInProgress ? 'Restoring...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Reclassification;